/**
 * Преобразует значение datetime, пришедшее с backend, в строку формата
 * `YYYY-MM-DDTHH:mm` (то, что ожидает `<input type="datetime-local">`).
 *
 * Поддерживает:
 * - ISO-8601 с `Z` (например, `2024-05-10T11:30:00Z`).
 * - ISO-8601 со смещением (например, `2024-05-10T11:30:00.123456+03:00`).
 * - Формат DRF `%d.%m.%Y %H:%M` (например, `10.05.2024 11:30`),
 *   который проект использует в `REST_FRAMEWORK['DATETIME_FORMAT']`.
 * - Уже локальный формат `YYYY-MM-DDTHH:mm` (на случай, если значение
 *   пришло из localStorage или было ранее нормализовано).
 * - Любой другой формат, который `Date` в состоянии распарсить.
 *
 * Возвращает `null` для пустых/невалидных значений (важно: именно `null`,
 * а не пустую строку — иначе Angular Material может оставить input
 * визуально пустым, а `Validators.required` будет трактовать пустую
 * строку как отсутствие значения).
 */
export function fromApiDateTimeToLocalInput(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  // Сначала пробуем DRF-формат `%d.%m.%Y %H:%M` (точка как разделитель даты).
  // Делаем это ДО `new Date(...)`, потому что `new Date('10.05.2024 11:30')`
  // не парсится в большинстве браузеров.
  const drfMatch = /^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
  if (drfMatch) {
    const [, dd, mm, yyyy, hh, mi] = drfMatch;
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }

  // Уже локальный формат (`YYYY-MM-DDTHH:mm` или `YYYY-MM-DDTHH:mm:ss`).
  const localMatch = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(trimmed);
  if (localMatch) {
    return `${localMatch[1]}-${localMatch[2]}-${localMatch[3]}T${localMatch[4]}:${localMatch[5]}`;
  }

  // Всё остальное (ISO с Z или смещением, нативные форматы браузера).
  const d = new Date(trimmed);
  if (isNaN(d.getTime())) return null;

  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${day}T${h}:${min}`;
}

/**
 * Парсит строковое значение даты (без времени) в локальный `Date`.
 * Понимает:
 *  - DRF-формат `%d.%m.%Y` (например, `"10.05.2024"`).
 *  - ISO `%Y-%m-%d` и с суффиксом времени/таймзоны
 *    (`2024-05-10`, `2024-05-10T00:00:00`, `2024-05-10T...Z`) — берётся
 *    только дата, интерпретируется как ЛОКАЛЬНАЯ (без сдвига UTC, как делает
 *    `new Date('YYYY-MM-DD')`).
 *  - `Date` (проходит как есть, невалидный → null).
 * Возвращает `null`, если значение пустое/невалидное.
 */
export function parseApiDate(value: string | Date | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  // DRF `dd.MM.yyyy`
  const drf = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(trimmed);
  if (drf) return new Date(Number(drf[3]), Number(drf[2]) - 1, Number(drf[1]));
  // ISO `YYYY-MM-DD` (с опц. временем/таймзоной) — дату трактуем как локальную
  const iso = /^(\d{4})-(\d{2})-(\d{2})([T ].*)?$/.exec(trimmed);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  // fallback
  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Парсит строку даты/времени в `Date`. Понимает:
 * - DRF-формат `%d.%m.%Y %H:%M` (например, `"02.07.2026 00:00"`) — нативно
 *   `new Date('02.07.2026 00:00')` возвращает Invalid Date в большинстве
 *   браузеров, поэтому сначала пробуем regex.
 * - ISO (`...Z`, `...+03:00`), локальный `YYYY-MM-DDTHH:mm` и прочие
 *   форматы, которые `Date` способен распарсить сам.
 *
 * Возвращает `null`, если строка не парсится ни одним способом.
 */
export function parseApiDateTime(dateStr: string): Date | null {
  const drfMatch = /^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(dateStr);
  if (drfMatch) {
    const [, dd, mm, yyyy, hh, mi] = drfMatch;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi));
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d;
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const date = parseApiDateTime(dateStr);
  if (!date) return dateStr;
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const date = parseApiDateTime(dateStr);
  if (!date) return dateStr;
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function toApiDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function toApiDateTime(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = date.getHours();
  const min = String(date.getMinutes()).padStart(2, '0');
  // Используем `padStart` для часов в обе стороны — симметрично и
  // гарантирует корректный формат при `getHours() < 10`.
  return `${y}-${m}-${d}T${String(h).padStart(2, '0')}:${min}`;
}

/**
 * Разбивает строковое представление даты+времени в формате API (ISO или
 * `dd.MM.yyyy HH:mm`) на пару (Date, 'HH:mm'). Возвращает `null`, если
 * вход не парсится.
 *
 * Используется для предзаполнения пары контролов `MatDatepicker + <input
 * type="time">` из API-значения.
 */
export function splitDateTimeToParts(
  value: string | null | undefined,
): { date: Date | null; time: string } | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  // DRF-формат `dd.MM.yyyy HH:mm[:ss]`
  const drf = /^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
  if (drf) {
    const [, dd, mm, yyyy, hh, mi] = drf;
    return { date: new Date(+yyyy, +mm - 1, +dd), time: `${hh}:${mi}` };
  }
  // Локальный `YYYY-MM-DDTHH:mm[:ss]`
  const local = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(trimmed);
  if (local) {
    const [, y, mo, d, h, mi] = local;
    return { date: new Date(+y, +mo - 1, +d), time: `${h}:${mi}` };
  }
  // ISO с Z или смещением
  const dt = new Date(trimmed);
  if (isNaN(dt.getTime())) return null;
  return {
    date: new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()),
    time: `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`,
  };
}

/**
 * Собирает строку `dd.MM.yyyy HH:mm` из Date-объекта даты и строки времени
 * `HH:mm`. Если `date` отсутствует, возвращает `null`.
 */
export function combineDateAndTimeToString(
  date: Date | null,
  time: string | null | undefined,
): string | null {
  if (!date) return null;
  const t = time && /^\d{2}:\d{2}/.test(time) ? time : '00:00';
  const [hh, mi] = t.split(':');
  const d = `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;
  return `${d} ${hh}:${mi}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
